import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, CodeBlock, CopyButton, ErrorState, Icon, Input, Segmented, Skeleton, cn, toast } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { formatRelative } from "../shared/format";
import type { InstallStatusOut, SnippetsOut } from "../shared/types";
import { EmailDevDialog } from "./email-dev-dialog";
import { IdentityGuide } from "./identity-guide";
import { IframeGuide, IframeNeedsSite } from "./iframe-guide";
import { MobileGuide } from "./mobile-guide";
import { useInstallStatus, useSnippets } from "./use-widget-studio";

/**
 * Iframe is offered only while the workspace lists at least one site that may frame the chat (HCU-002, HCU-006): without a listed
 * site every /chat response sends frame-ancestors 'none', so a frame could not render it (BUG-SCOPE-003). Otherwise a note says how to add a site.
 */
type Method = "html" | "react" | "iframe" | "wordpress" | "portal" | "mobile" | "identity";
const METHODS: readonly Method[] = ["html", "react", "iframe", "wordpress", "portal", "mobile", "identity"];
const K = "deployKnowledge.widgetStudio.install";

function InstallStatusBanner({ agentId, initial }: { agentId: string; initial: InstallStatusOut }) {
  const { t, i18n } = useTranslation();
  const status = useInstallStatus(agentId, initial);
  const data = status.data ?? initial;
  const recheck = () => {
    void status.refetch().then(() => toast.success(t(`${K}.rechecked`)));
  };
  const origin = data.last_seen_origin?.replace(/^https?:\/\//, "");
  return (
    <div className={cn("flex items-center gap-3 rounded-lg border border-line p-3.5", data.installed ? "bg-success-soft" : "bg-surface-2")} data-testid="install-status">
      <Icon name={data.installed ? "check-circle" : "info"} className={cn("size-5 shrink-0", data.installed ? "text-success" : "text-fg-subtle")} />
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold">{data.installed && origin ? t(`${K}.detected`, { origin }) : t(`${K}.notDetected`)}</div>
        <div className="text-xs text-fg-muted">
          {data.installed ? t(`${K}.lastSeen`, { when: formatRelative(data.last_seen_at, i18n.language), count: data.origins.length }) : t(`${K}.notDetectedHint`)}
        </div>
      </div>
      <Button variant="ghost" size="sm" loading={status.isFetching} onClick={recheck}>
        <Icon name="refresh-cw" />
        {t(`${K}.recheck`)}
      </Button>
    </div>
  );
}

function StepList({ steps }: { steps: readonly string[] }) {
  return (
    <ol className="space-y-3 text-sm">
      {steps.map((step, index) => (
        <li key={step} className="flex gap-3">
          <span aria-hidden="true" className="inline-flex size-6 shrink-0 items-center justify-center rounded-pill bg-primary-soft text-xs font-bold text-primary-ink">
            {index + 1}
          </span>
          <span className="min-w-0 break-words">{step}</span>
        </li>
      ))}
    </ol>
  );
}

function Snippet({ method, snippets, settingsHref }: { method: Method; snippets: SnippetsOut; settingsHref: string }) {
  const { t } = useTranslation();
  const label = t(`${K}.snippetLabel`, { method: t(`${K}.${method}`) });
  const copy = { copyLabel: t("common.copy"), copiedLabel: t("common.copied") };
  if (method === "html") return <MethodBody hint={t(`${K}.htmlHint`)}><CodeBlock code={snippets.html} label={label} {...copy} /></MethodBody>;
  if (method === "react") return <MethodBody after={t(`${K}.reactHint`)}><CodeBlock code={snippets.react} label={label} {...copy} /></MethodBody>;
  if (method === "iframe" && snippets.iframe) return <IframeGuide iframe={snippets.iframe} origins={snippets.frame_origins} chatOrigin={originOf(snippets.script_url)} settingsHref={settingsHref} />;
  if (method === "portal") return <MethodBody><StepList steps={snippets.portal} /></MethodBody>;
  if (method === "identity") return <IdentityGuide examples={snippets.identity} />;
  if (method === "mobile") return <MobileGuide examples={snippets.mobile} />;
  return (
    <MethodBody>
      <StepList steps={snippets.wordpress} />
      <div className="flex gap-2">
        <Input readOnly className="font-mono text-xs" value={snippets.agent_key} aria-label={t(`${K}.agentId`)} />
        <CopyButton value={snippets.agent_key} label={t("common.copy")} copiedLabel={t("common.copied")} className="btn-secondary" />
      </div>
    </MethodBody>
  );
}

/** `https://app.example.com/widget.js` -> `https://app.example.com` (the origin the frame loads the chat from). */
function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return url;
  }
}

function MethodBody({ hint, after, children }: { hint?: string; after?: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      {hint ? <p className="text-xs text-fg-muted">{hint}</p> : null}
      {children}
      {after ? <p className="text-xs text-fg-muted">{after}</p> : null}
    </div>
  );
}

export function InstallPanel({ agentId, install }: { agentId: string; install: InstallStatusOut }) {
  const { t } = useTranslation();
  const [method, setMethod] = useState<Method>("html");
  const [emailOpen, setEmailOpen] = useState(false);
  const snippets = useSnippets(agentId, true);
  const { summary } = useWorkspaceContext();
  const settingsHref = consoleRoutes.settings(summary.slug, "secure-launch");
  const canFrame = (snippets.data?.frame_origins.length ?? 0) > 0 && Boolean(snippets.data?.iframe);
  const methods = METHODS.filter((value) => value !== "iframe" || canFrame);
  const shown: Method = methods.includes(method) ? method : "html";
  return (
    <div className="space-y-5">
      <InstallStatusBanner agentId={agentId} initial={install} />
      <Segmented
        className="flex w-full flex-wrap"
        aria-label={t(`${K}.methods`)}
        value={shown}
        onValueChange={setMethod}
        options={methods.map((value) => ({ value, label: t(`${K}.${value}`) }))}
      />
      {snippets.data && !canFrame ? <IframeNeedsSite settingsHref={settingsHref} /> : null}
      {snippets.isPending ? (
        <Skeleton className="h-40" />
      ) : snippets.isError ? (
        <ErrorState title={t(`${K}.snippetsError`)} onRetry={() => void snippets.refetch()} retryLabel={t("common.retry")} />
      ) : (
        <Snippet method={shown} snippets={snippets.data} settingsHref={settingsHref} />
      )}
      {snippets.data ? (
        <a href={snippets.data.guide_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary-ink hover:underline">
          <Icon name="external-link" className="size-3.5" />
          {t(`${K}.guide`)}
        </a>
      ) : null}
      <Button variant="secondary" className="w-full" onClick={() => { setEmailOpen(true); }}>
        <Icon name="mail" />
        {t(`${K}.emailDev`)}
      </Button>
      <EmailDevDialog agentId={agentId} open={emailOpen} onOpenChange={setEmailOpen} />
    </div>
  );
}
